import { useEffect, useState, useMemo, useRef } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import {
  Box,
  TextField,
  Typography,
} from "@mui/material";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import type { AddressSuggestionDto } from "../../../generated/models";
import { useAddressBookApi } from "../context";
import { formatAddressSuggestion } from "../utils/addressFormatting";

interface AddressLookupProps {
  onSuggestionSelected?: (suggestion: AddressSuggestionDto) => void;
  disabled?: boolean;
}

export default function AddressLookup({
  onSuggestionSelected,
  disabled = false,
}: Readonly<AddressLookupProps>) {
  const api = useAddressBookApi();
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data, isError } = api.useAddressLookup(debouncedQuery);

  const suggestions = useMemo(
    () => data?.suggestions ?? [],
    [data?.suggestions],
  );

  const topSuggestion = suggestions.length > 0 ? suggestions[0] : null;
  const topSuggestionText = topSuggestion ? formatAddressSuggestion(topSuggestion) : "";

  // Show inline completion text only when the user has typed at least 3 characters and there is a suggestion to show.
  const trimmedInput = inputValue.trim();
  const showTypeahead = trimmedInput.length >= 3;
  const completionText = showTypeahead ? topSuggestionText : "";

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, 300);
  };

  const acceptSuggestion = () => {
    if (topSuggestion && showTypeahead) {
      onSuggestionSelected?.(topSuggestion);
      setInputValue("");
      setDebouncedQuery("");
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" && showTypeahead) {
      e.preventDefault();
      acceptSuggestion();
    }
  };

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ mb: 0.5, display: "block" }}
      >
        Address Lookup
      </Typography>
      <Box sx={{ position: "relative" }}>
        {/* Ghost text layer */}
        {completionText && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              px: 5.5,
              color: "text.disabled",
              fontSize: "1rem",
              lineHeight: "1.4375em",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ visibility: "hidden" }}>{inputValue}</span>
            <span>{completionText.slice(inputValue.length)}</span>
          </Box>
        )}

        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          fullWidth
          size="small"
          placeholder="Start typing an address..."
          slotProps={{
            input: {
              startAdornment: (
                <MyLocationOutlinedIcon
                  sx={{ color: "action.active", mr: 1 }}
                  fontSize="small"
                />
              ),
              sx: { bgcolor: "transparent" },
            },
          }}
          aria-label="Address lookup"
        />
      </Box>
      {showTypeahead && topSuggestion && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Press Tab to accept suggestion
        </Typography>
      )}
      {isError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          Unable to search addresses
        </Typography>
      )}
    </Box>
  );
}
